import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Table, Button, Form, Alert, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AsyncSelect from "react-select/async";
import { authFetch } from "../utils/authFetch";
import StocCurentPickerModal from "../pickers/StocCurentPickerModal";
import { toast } from "react-toastify";

const toISODateTimeLocal = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    dt.getFullYear() +
    "-" +
    pad(dt.getMonth() + 1) +
    "-" +
    pad(dt.getDate()) +
    "T" +
    pad(dt.getHours()) +
    ":" +
    pad(dt.getMinutes()) +
    ":" +
    pad(dt.getSeconds())
  );
};

const num = (v) => {
  const x = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(x) ? x : 0;
};

const makeUid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `uid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const emptyLine = () => ({
  uid: makeUid(),
  id: null,
  bxpart_id: null,
  part_label: "",
  qty: "",
  cantitate_reteta: 0,
  available: null,
  loadingAvail: false,
});

export function BonProductieForm({ initialDoc, onSubmit, busy, isClosed, onChangePayload }) {
  const [err, setErr] = useState("");
  const [showPartModal, setShowPartModal] = useState(false);
  const [partLineUid, setPartLineUid] = useState(null);

  const openPartForLine = (uid) => {
    if (!selGestiune?.value) {
      setErr("Selectează gestiunea de materii prime mai întâi.");
      return;
    }
    setPartLineUid(uid);
    setShowPartModal(true);
  };

  const handlePickPart = (opt) => {
    if (!partLineUid) return;
    handlePartChange(partLineUid, { value: opt.id, label: opt.label, raw: opt.raw });
    setShowPartModal(false);
    setPartLineUid(null);
  };

  const [selContact, setSelContact] = useState(null);
  const [docNo, setDocNo] = useState(initialDoc?.bxdoc_no || "");
  const [docDate, setDocDate] = useState(() => {
    const v = initialDoc?.bxdoc_date;
    return v ? new Date(v) : new Date();
  });

  const [selGestiune, setSelGestiune] = useState(null);
  const [selGestiune2, setSelGestiune2] = useState(null);
  const [selProdusFinit, setSelProdusFinit] = useState(null);
  const [qtyProdusa, setQtyProdusa] = useState(initialDoc?.cantitate_produsa || 1);
  const [observatii, setObservatii] = useState(initialDoc?.observatii || "");
  const [recipeInfo, setRecipeInfo] = useState("");

  const [lines, setLines] = useState(() => {
    const lns = initialDoc?.linii_lns || initialDoc?.linii || [];
    if (!lns.length) return [emptyLine()];
    return lns.map((x) => ({
      uid: makeUid(),
      id: x.id ?? null,
      bxpart_id: x.bxpart_id ?? null,
      part_label: x.part_label || x.denumire || (x.bxpart_id ? `Material #${x.bxpart_id}` : ""),
      qty: x.qty ?? "",
      cantitate_reteta: 0,
      available: null,
      loadingAvail: false,
    }));
  });

  const lastAvailKeyRef = useRef(new Map());
  const reqSeqRef = useRef(new Map());

  const makeAvailKey = (gest, dateObj, partId) => {
    const d = dateObj ? toISODateTimeLocal(dateObj).slice(0, 10) : "";
    return `${gest ?? ""}|${d}|${partId ?? ""}`;
  };

  const fetchGestiuneById = async (id) => {
    if (!id) return null;
    try {
      const data = await authFetch(`/bxgestiune/${id}`);
      const label = data?.bxnume || data?.denumire || data?.name || `Gestiune #${id}`;
      return { value: id, label };
    } catch (e) {
      return { value: id, label: `Gestiune #${id}` };
    }
  };

  const fetchPartById = async (id) => {
    if (!id) return null;
    try {
      const data = await authFetch(`/bxpart/${id}`);
      return `${data?.cod || ""} ${data?.denumire || ""}`.trim() || `Part #${id}`;
    } catch (e) {
      return `Part #${id}`;
    }
  };

  const fetchContactById = async (id) => {
    if (!id) return null;
    try {
      const data = await authFetch(`/contacte/${id}`);
      const label = data?.bxdenumire || data?.denumire || data?.name || `Contact #${id}`;
      return { value: id, label };
    } catch (e) {
      return { value: id, label: `Contact #${id}` };
    }
  };

  // Load initial fields if edit mode
  useEffect(() => {
    if (initialDoc?.bxgestiune_id && !selGestiune) {
      fetchGestiuneById(initialDoc.bxgestiune_id).then((opt) => opt && setSelGestiune(opt));
    }
    if (initialDoc?.bxgestiune2_id && !selGestiune2) {
      fetchGestiuneById(initialDoc.bxgestiune2_id).then((opt) => opt && setSelGestiune2(opt));
    }
    if (initialDoc?.produs_finit_id && !selProdusFinit) {
      fetchPartById(initialDoc.produs_finit_id).then((lbl) => {
        setSelProdusFinit({ value: initialDoc.produs_finit_id, label: lbl });
      });
    }
    if (initialDoc?.contact_id && !selContact) {
      fetchContactById(initialDoc.contact_id).then((opt) => opt && setSelContact(opt));
    }
    if (initialDoc?.cantitate_produsa) {
      setQtyProdusa(initialDoc.cantitate_produsa);
    }
    if (initialDoc?.bxdoc_no) {
      setDocNo(initialDoc.bxdoc_no);
    }
  }, [initialDoc]);

  // Preload line labels
  useEffect(() => {
    let active = true;
    const linesToFetch = lines.filter(
      (ln) => ln.bxpart_id && (!ln.part_label || ln.part_label.startsWith("Material #"))
    );

    if (linesToFetch.length === 0) return;

    const fetchAllLabels = async () => {
      const updatedLines = await Promise.all(
        lines.map(async (ln) => {
          if (ln.bxpart_id && (!ln.part_label || ln.part_label.startsWith("Material #"))) {
            const label = await fetchPartById(ln.bxpart_id);
            return { ...ln, part_label: label };
          }
          return ln;
        })
      );
      if (active) {
        setLines(updatedLines);
      }
    };

    fetchAllLabels();
    return () => {
      active = false;
    };
  }, [initialDoc]);

  // Fetch active recipe when finished product changes
  const loadActiveRecipe = async (finitId, scaleQty) => {
    if (!finitId) return;
    try {
      const res = await authFetch(`/bxreteta/activa/${finitId}`);
      if (res && res.id) {
        setRecipeInfo(`Rețetă activă: ${res.cod_reteta} (${res.denumire})`);
        
        const mappedLines = (res.linii || []).map((l) => {
          const c_reteta = num(l.cantitate);
          return {
            uid: makeUid(),
            id: null,
            bxpart_id: l.material_id,
            part_label: `${l.material_cod_str || ""} ${l.material_denumire || ""}`.trim() || `Material #${l.material_id}`,
            cantitate_reteta: c_reteta,
            qty: String(c_reteta * scaleQty),
            available: null,
            loadingAvail: false,
          };
        });
        
        setLines(mappedLines.length ? mappedLines : [emptyLine()]);
      } else {
        setRecipeInfo("");
        toast.info("Acest produs finit nu are nicio rețetă activă.");
      }
    } catch (e) {
      console.warn("Failed to load active recipe:", e);
      setRecipeInfo("");
    }
  };

  // Recalculate quantities when qtyProdusa changes (if recipe lines loaded)
  const handleQtyProdusaChange = (newVal) => {
    const val = num(newVal);
    setQtyProdusa(newVal);
    
    setLines((prev) =>
      prev.map((ln) => {
        if (ln.cantitate_reteta > 0) {
          return {
            ...ln,
            qty: String(Number((ln.cantitate_reteta * val).toFixed(5))),
          };
        }
        return ln;
      })
    );
  };

  // Real-time stock check
  const checkStock = async (uid, gestId, dateObj, partId) => {
    if (!gestId || !partId) {
      setLines((prev) =>
        prev.map((l) => (l.uid === uid ? { ...l, available: null, loadingAvail: false } : l))
      );
      return;
    }

    const key = makeAvailKey(gestId, dateObj, partId);
    if (lastAvailKeyRef.current.get(uid) === key) {
      return; // already checked/checking
    }
    lastAvailKeyRef.current.set(uid, key);

    const seq = (reqSeqRef.current.get(uid) || 0) + 1;
    reqSeqRef.current.set(uid, seq);

    setLines((prev) =>
      prev.map((l) => (l.uid === uid ? { ...l, loadingAvail: true } : l))
    );

    try {
      const dStr = dateObj ? toISODateTimeLocal(dateObj) : "";
      const url = `/stock/available?bxpart_id=${partId}&gestiune_id=${gestId}&as_of=${encodeURIComponent(dStr)}`;
      const res = await authFetch(url);
      
      if (reqSeqRef.current.get(uid) !== seq) return; // stale request

      const val = typeof res === "number" ? res : (res?.qty ?? res?.available ?? 0);

      setLines((prev) =>
        prev.map((l) =>
          l.uid === uid
            ? { ...l, available: val, loadingAvail: false }
            : l
        )
      );
    } catch (e) {
      if (reqSeqRef.current.get(uid) !== seq) return;
      setLines((prev) =>
        prev.map((l) => (l.uid === uid ? { ...l, available: 0, loadingAvail: false } : l))
      );
    }
  };

  useEffect(() => {
    lines.forEach((ln) => {
      if (ln.bxpart_id && selGestiune?.value) {
        checkStock(ln.uid, selGestiune.value, docDate, ln.bxpart_id);
      }
    });
  }, [selGestiune, docDate, lines]);

  const loadGestiuni = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const data = await authFetch(`/bxgestiune/?skip=0&limit=20&q=${encodeURIComponent(q)}`);
      const items = data?.items || data || [];
      return items.map((g) => ({
        value: g.id,
        label: `${g.cod || ""} ${g.bxnume || g.denumire || g.name}`.trim(),
      }));
    } catch (e) {
      return [];
    }
  };

  const loadParts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const data = await authFetch(`/bxpart/?skip=0&limit=20&q=${encodeURIComponent(q)}`);
      const items = data?.items || data || [];
      return items.map((p) => ({
        value: p.id,
        label: `${p.cod || ""} — ${p.denumire}`.trim(),
        __raw: p,
      }));
    } catch (e) {
      return [];
    }
  };

  const loadContacts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const data = await authFetch(`/contacte/?skip=0&limit=20&q=${encodeURIComponent(q)}`);
      const items = data?.items || data || [];
      return items.map((c) => ({
        value: c.id,
        label: `${c.bxcod || ""} ${c.bxdenumire || c.denumire || c.name}`.trim(),
      }));
    } catch (e) {
      return [];
    }
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (uid) => {
    setLines((prev) => {
      const filtered = prev.filter((l) => l.uid !== uid);
      return filtered.length ? filtered : [emptyLine()];
    });
    lastAvailKeyRef.current.delete(uid);
    reqSeqRef.current.delete(uid);
  };

  const handlePartChange = (uid, opt) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.uid === uid) {
          const partId = opt?.value ?? null;
          return {
            ...l,
            bxpart_id: partId,
            part_label: opt?.label ?? "",
            available: null,
          };
        }
        return l;
      })
    );
  };

  const handleQtyChange = (uid, val) => {
    setLines((prev) =>
      prev.map((l) => (l.uid === uid ? { ...l, qty: val } : l))
    );
  };

  // Trigger state updates up to parent
  useEffect(() => {
    if (onChangePayload) {
      const cleanLines = lines
        .filter((l) => l.bxpart_id && num(l.qty) > 0)
        .map((l) => ({
          id: l.id ?? null,
          bxpart_id: l.bxpart_id,
          qty: num(l.qty),
        }));

      onChangePayload({
        bxdoc_no: docNo || null,
        bxdoc_date: docDate.toISOString(),
        bxgestiune_id: selGestiune?.value || null,
        bxgestiune2_id: selGestiune2?.value || null,
        produs_finit_id: selProdusFinit?.value || null,
        cantitate_produsa: num(qtyProdusa),
        contact_id: selContact?.value || null,
        observatii: observatii || "",
        linii: cleanLines,
      });
    }
  }, [
    docNo,
    docDate,
    selGestiune,
    selGestiune2,
    selProdusFinit,
    qtyProdusa,
    selContact,
    observatii,
    lines,
    onChangePayload,
  ]);

  const handleSave = (e) => {
    e.preventDefault();
    if (!selGestiune?.value) {
      setErr("Alege gestiunea de materii prime (sursă).");
      return;
    }
    if (!selGestiune2?.value) {
      setErr("Alege gestiunea de produse finite (destinație).");
      return;
    }
    if (!selProdusFinit?.value) {
      setErr("Alege produsul finit.");
      return;
    }
    if (!selContact?.value) {
      setErr("Alege beneficiarul (contact).");
      return;
    }
    if (num(qtyProdusa) <= 0) {
      setErr("Cantitatea produsă trebuie să fie mai mare ca 0.");
      return;
    }

    const cleanLines = lines
      .filter((l) => l.bxpart_id && num(l.qty) > 0)
      .map((l) => ({
        id: l.id ?? null,
        bxpart_id: l.bxpart_id,
        qty: num(l.qty),
      }));

    if (cleanLines.length === 0) {
      setErr("Trebuie să adaugi cel puțin o materie primă cu cantitate pozitivă.");
      return;
    }

    setErr("");
    onSubmit({
      bxdoc_no: docNo || null,
      bxdoc_date: docDate.toISOString(),
      bxgestiune_id: selGestiune.value,
      bxgestiune2_id: selGestiune2.value,
      produs_finit_id: selProdusFinit.value,
      cantitate_produsa: num(qtyProdusa),
      contact_id: selContact.value,
      observatii: observatii || "",
      linii: cleanLines,
    });
  };

  return (
    <Form onSubmit={handleSave}>
      {err && <Alert variant="danger">{err}</Alert>}

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Număr document</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex: BP0001"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              disabled={isClosed}
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="d-flex flex-column">
            <Form.Label>Dată document</Form.Label>
            <DatePicker
              selected={docDate}
              onChange={setDocDate}
              dateFormat="dd.MM.yyyy"
              className="form-control"
              disabled={isClosed}
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Gestiune Materii Prime (Sursă)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={selGestiune}
              loadOptions={loadGestiuni}
              onChange={setSelGestiune}
              placeholder="Alege gestiune..."
              isDisabled={isClosed}
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Gestiune Produse Finite (Destinatie)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={selGestiune2}
              loadOptions={loadGestiuni}
              onChange={setSelGestiune2}
              placeholder="Alege gestiune..."
              isDisabled={isClosed}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3 align-items-end">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Produs Finit Produs</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={selProdusFinit}
              loadOptions={loadParts}
              onChange={(opt) => {
                setSelProdusFinit(opt);
                if (opt?.value) {
                  loadActiveRecipe(opt.value, num(qtyProdusa));
                } else {
                  setRecipeInfo("");
                }
              }}
              placeholder="Caută după denumire/cod..."
              isDisabled={isClosed}
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Cantitate Produsă</Form.Label>
            <Form.Control
              type="number"
              step="any"
              value={qtyProdusa}
              onChange={(e) => handleQtyProdusaChange(e.target.value)}
              disabled={isClosed}
              required
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          {recipeInfo && (
            <div className="text-success fw-bold pb-2">
              ✅ {recipeInfo}
            </div>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Beneficiar (Contact)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={selContact}
              loadOptions={loadContacts}
              onChange={setSelContact}
              placeholder="Alege beneficiar..."
              isDisabled={isClosed}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Observații</Form.Label>
            <Form.Control
              type="text"
              value={observatii}
              onChange={(e) => setObservatii(e.target.value)}
              disabled={isClosed}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <h5 className="m-0 text-secondary">Materiale Consumate (Materii Prime)</h5>
        {!isClosed && (
          <Button variant="outline-primary" size="sm" onClick={addLine} disabled={busy}>
            ➕ Adaugă Material
          </Button>
        )}
      </div>

      <Table bordered size="sm" className="align-middle">
        <thead className="table-dark">
          <tr>
            <th>Material / Componentă</th>
            <th style={{ width: 150 }} className="text-end">Stoc Disponibil</th>
            <th style={{ width: 150 }} className="text-end">Cantitate Consum</th>
            {!isClosed && <th style={{ width: 90 }} className="text-center">Acțiuni</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => (
            <tr key={ln.uid}>
              <td>
                <div
                  role="button"
                  onClick={() => !isClosed && openPartForLine(ln.uid)}
                  className={`w-100 ${isClosed ? "pe-none" : ""}`}
                  style={{ cursor: isClosed ? "default" : "pointer" }}
                >
                  <Form.Control
                    readOnly
                    disabled={isClosed}
                    value={ln.part_label || ""}
                    placeholder="Click pentru a alege material din stoc/catalog..."
                  />
                </div>
              </td>
              <td className="text-end font-monospace">
                {ln.loadingAvail ? (
                  <Spinner animation="border" size="sm" />
                ) : ln.available == null ? (
                  <span className="text-muted">—</span>
                ) : (
                  <strong className={Number(ln.available) < num(ln.qty) ? "text-danger" : "text-success"}>
                    {Number(ln.available).toFixed(4)}
                  </strong>
                )}
              </td>
              <td>
                <Form.Control
                  type="number"
                  step="any"
                  className="text-end font-monospace"
                  disabled={isClosed}
                  value={ln.qty}
                  onChange={(e) => handleQtyChange(ln.uid, e.target.value)}
                  placeholder="0.0000"
                  required
                />
              </td>
              {!isClosed && (
                <td className="text-center">
                  <Button variant="outline-danger" size="sm" onClick={() => removeLine(ln.uid)}>
                    🗑️
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {!isClosed && (
        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" /> : "Salvează Document"}
          </Button>
        </div>
      )}

      {showPartModal && (
        <StocCurentPickerModal
          show={showPartModal}
          onHide={() => {
            setShowPartModal(false);
            setPartLineUid(null);
          }}
          gestiuneId={selGestiune?.value}
          onPick={handlePickPart}
        />
      )}
    </Form>
  );
}
